import { useEffect, useMemo, useState, useRef, useCallback } from "react";
import LecturerSidebar from "../components/LecturerSidebar.js";
import "../styles/LecturerGrades.css";
import { GraduationCap, Pencil, Trash, Eye, EyeOff, X } from "lucide-react";
import GradeModal from "../components/GradeModal.js";
import AssignmentModal from "../components/AssignmentModal.js";
import EditGradeModal from "../components/EditGradeModal.js";
import { displayGrade } from "../utils/gradeValues";
import { getErrorMessage, getFetchErrorMessage } from "../utils/errorUtils";
import ToastNotification from "../components/ToastNotification.js";

function isNumericGrade(v) {
    if (v === null || v === undefined) return false;
    const s = String(v).trim();
    if (!s) return false;
    if (s.toUpperCase() === "NP" || s.toUpperCase() === "ZW") return false;
    const n = Number(s.replace(",", "."));
    return !Number.isNaN(n);
}

function toNumber(v) {
    const n = Number(String(v).replace(",", "."));
    return Number.isNaN(n) ? null : n;
}

function safeText(v) {
    return v === null || v === undefined || String(v).trim() === "" ? "—" : String(v);
}

function showGrade(g) {
    if (g === null || g === undefined || g === "") {
        return "—";
    }
    if (typeof g === "object") {
        return displayGrade(g);
    }
    return safeText(g);
}

function normalizeType(t) {
    return String(t || "").toLowerCase();
}

// Activity types mapping (matching GradeModal.js)
const activityTypes = [
    { value: 'PRESENTATION', label: 'Prezentacja' },
    { value: 'ORAL', label: 'Prezentacja' }, // Backend compatibility
    { value: 'QUIZ', label: 'Quiz' },
    { value: 'ACTIVITY', label: 'Aktywność' },
    { value: 'TEST', label: 'Test' },
    { value: 'OTHER', label: 'Inne' }
];

function getActivityTypeLabel(activityType) {
    if (!activityType) return 'Aktywność';
    const type = activityTypes.find(t => t.value === activityType.toUpperCase());
    return type ? type.label : activityType;
}

function getGroupKey(g) {
    // Spróbuj różnych pól jeśli backend je ma
    return (
        g.group_code ||
        g.groupCode ||
        g.group_name ||
        g.groupName ||
        g.group_id ||
        g.groupId ||
        ""
    );
}

function getGroupLabel(g) {
    const code = g.group_code || g.groupCode;
    const name = g.group_name || g.groupName;
    if (name && code) return `${name} (${code})`;
    return name || code || "";
}

function sortByDateAsc(a, b) {
    const da = String(a.grade_date || "");
    const db = String(b.grade_date || "");
    if (da < db) return -1;
    if (da > db) return 1;
    return 0;
}

function computeFinalAverage(allGrades) {
    // Calculate average from all numeric grades, excluding final grades
    const nums = [];
    allGrades.forEach((g) => {
        const type = normalizeType(g.grade_type || "");
        // Exclude final grades from average calculation
        if (!type.includes("końc") && !type.includes("final")) {
            if (isNumericGrade(g.grade)) {
                const num = toNumber(g.grade);
                if (num !== null && Number.isFinite(num)) {
                    nums.push(num);
                }
            }
        }
    });

    if (!nums.length) return null;

    const avg = nums.reduce((a, b) => a + b, 0) / nums.length;
    return avg;
}

export default function LecturerGrades() {
    const [grades, setGrades] = useState([]);
    const [loadingGrades, setLoadingGrades] = useState(true);

    const [showCreateGradeModal, setShowCreateGradeModal] = useState(false);
    const [showCreateAssignmentModal, setShowCreateAssignmentModal] = useState(false);
    const [editingGrade, setEditingGrade] = useState(null);
    const [refreshTrigger, setRefreshTrigger] = useState(0);
    const [notification, setNotification] = useState({ show: false, message: "", type: "success" });
    const notificationTimerRef = useRef(null);

    const showNotification = useCallback((message, type = "success") => {
        if (notificationTimerRef.current) {
            clearTimeout(notificationTimerRef.current);
        }
        const text =
            type === "error"
                ? getErrorMessage(message, "Wystąpił błąd. Spróbuj ponownie.")
                : message;
        setNotification({ show: true, message: text, type });
        notificationTimerRef.current = setTimeout(() => {
            setNotification({ show: false, message: "", type: "success" });
        }, 4000);
    }, []);

    useEffect(() => {
        return () => {
            if (notificationTimerRef.current) {
                clearTimeout(notificationTimerRef.current);
            }
        };
    }, []);

    // UI state
    const [selectedGroup, setSelectedGroup] = useState("");
    const [selectedModule, setSelectedModule] = useState("");
    const [query, setQuery] = useState("");
    const [showPartials, setShowPartials] = useState(true);

    // „Szczegóły” dla wiersza (lista wpisów ocen)
    const [detailsKey, setDetailsKey] = useState(null);

    useEffect(() => {
        const fetchGrades = async () => {
            try {
                const token = localStorage.getItem("token");

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/lecturer/grades/allGrades`,
                    {
                        method: "GET",
                        headers: { Authorization: `Bearer ${token}` },
                    }
                );

                if (response.ok) {
                    const data = await response.json();
                    setGrades(Array.isArray(data) ? data : []);
                } else {
                    const msg = await getFetchErrorMessage(response, "Nie udało się pobrać ocen.");
                    showNotification(new Error(msg), "error");
                    setGrades([]);
                }
            } catch (error) {
                console.error("Error fetching grades:", error);
                showNotification(error, "error");
                setGrades([]);
            } finally {
                setLoadingGrades(false);
            }
        };

        fetchGrades();
    }, [refreshTrigger, showNotification]);

    const handleDeleteGrade = async (grade) => {
        if (
            !window.confirm(
                `Czy na pewno chcesz usunąć ocenę ${showGrade(grade)} dla ${grade.first_name} ${grade.last_name}?`
            )
        )
            return;

        try {
            const token = localStorage.getItem("token");
            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/lecturer/grades/${grade.id}`,
                {
                    method: "DELETE",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (!response.ok) {
                const msg = await getFetchErrorMessage(response, "Nie udało się usunąć oceny.");
                throw new Error(msg);
            }

            const data = await response.json();
            setGrades(data.grades || []);
            showNotification(data.message || "Usunięto ocenę", "success");
        } catch (err) {
            console.error("Błąd przy usuwaniu oceny:", err);
            showNotification(err, "error");
        }
    };

    const handleSaveGrade = async (updatedGrade) => {
        try {
            const token = localStorage.getItem("token");
            
            // Determine grade type from id
            const gradeId = updatedGrade.id || '';
            const isExam = gradeId.startsWith('exam_');
            const isAssignment = gradeId.startsWith('assignment_');
            const isActivity = gradeId.startsWith('activity_');

            // Build request body based on grade type
            let requestBody = {
                grade: updatedGrade.grade,
                grade_format: updatedGrade.grade_format,
                gradeDate: updatedGrade.grade_date,
            };

            if (isExam && updatedGrade.exam_type && updatedGrade.exam_status !== undefined) {
                requestBody.examType = updatedGrade.exam_type;
                let examStatus = updatedGrade.exam_status;
                if (updatedGrade.grade_format === 'pass_fail' && updatedGrade.grade) {
                    examStatus = updatedGrade.grade === 'PASSED' ? 'PASSED' : 'FAILED';
                }
                requestBody.examStatus = examStatus;
                requestBody.attempt = updatedGrade.attempt || 1;
            } else if (isAssignment && updatedGrade.feedback !== undefined) {
                requestBody.feedback = updatedGrade.feedback || null;
            } else if (isActivity && updatedGrade.activity_type) {
                requestBody.activityType = updatedGrade.activity_type;
                requestBody.weight = updatedGrade.weight || 1.0;
                requestBody.description = updatedGrade.description || null;
            }

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/lecturer/grades/${updatedGrade.id}`,
                {
                    method: "PUT",
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(requestBody),
                }
            );

            if (!response.ok) {
                const msg = await getFetchErrorMessage(response, "Nie udało się zaktualizować oceny.");
                throw new Error(msg);
            }

            const data = await response.json();
            setGrades(data.grades || []);
            setEditingGrade(null);
            showNotification(data.message || "Zapisano", "success");
        } catch (err) {
            console.error(err);
            showNotification(err, "error");
        }
    };

    // Opcje grup (tylko jeśli API zwraca coś o grupie)
    const groupOptions = useMemo(() => {
        const map = new Map();
        grades.forEach((g) => {
            const key = getGroupKey(g);
            const label = getGroupLabel(g);
            if (key && label) map.set(key, label);
        });
        const arr = Array.from(map.entries()).map(([value, label]) => ({ value, label }));
        arr.sort((a, b) => a.label.localeCompare(b.label, "pl"));
        return arr;
    }, [grades]);

    const moduleOptions = useMemo(() => {
        const set = new Set();
        grades.forEach((g) => {
            if (g.module_name) set.add(g.module_name);
        });
        return Array.from(set).sort((a, b) => a.localeCompare(b, "pl"));
    }, [grades]);

    // Dane zgrupowane: 1 wiersz = (student_number + module_name + grupa jeśli istnieje)
    const rows = useMemo(() => {
        const q = query.trim().toLowerCase();

        // filtrowanie rekordów
        const filtered = grades.filter((g) => {
            const groupKey = getGroupKey(g);
            if (selectedGroup && groupKey !== selectedGroup) return false;
            if (selectedModule && String(g.module_name || "") !== selectedModule) return false;

            if (!q) return true;
            const name = `${g.first_name || ""} ${g.last_name || ""}`.toLowerCase();
            const idx = String(g.student_number || "").toLowerCase();
            const mod = String(g.module_name || "").toLowerCase();
            return name.includes(q) || idx.includes(q) || mod.includes(q);
        });

        const grouped = new Map();

        filtered.forEach((g) => {
            const groupKey = getGroupKey(g) || "";
            const studentKey = String(g.student_number || g.student_id || "");
            const moduleKey = String(g.module_name || "");

            const key = `${groupKey}__${studentKey}__${moduleKey}`;

            if (!grouped.has(key)) {
                grouped.set(key, {
                    key,
                    groupKey,
                    groupLabel: getGroupLabel(g) || "",
                    studentName: `${g.first_name || ""} ${g.last_name || ""}`.trim(),
                    studentNumber: String(g.student_number || ""),
                    moduleName: moduleKey,
                    partials: [], // list wpisów "zadanie" i aktywności
                    exams: {}, // egzaminy pogrupowane według exam_type (FINAL, MIDTERM, RESIT)
                    finalGrade: null, // wpis "ocena końcowa" (ostatnia)
                    all: [], // wszystkie wpisy (do modala)
                    lastDate: "",
                });
            }

            const row = grouped.get(key);
            row.all.push(g);

            const dt = String(g.grade_date || "");
            if (dt && (!row.lastDate || dt > row.lastDate)) row.lastDate = dt;

            const type = normalizeType(g.grade_type);
            // Check if it's an assignment (Zadanie) or activity (has activity_type field)
            if (type.includes("zad") || g.activity_type) {
                // Assignments and activities go to partials
                row.partials.push(g);
            } else if (type.includes("egz")) {
                // Grupujemy egzaminy według exam_type (FINAL, MIDTERM, RESIT)
                const examType = g.exam_type || 'FINAL';
                if (!row.exams[examType]) {
                    row.exams[examType] = [];
                }
                row.exams[examType].push(g);
                // Sortujemy według attempt (rosnąco), a następnie według daty
                row.exams[examType].sort((a, b) => {
                    const attemptA = a.attempt || 1;
                    const attemptB = b.attempt || 1;
                    if (attemptA !== attemptB) return attemptA - attemptB;
                    const dateA = String(a.grade_date || "");
                    const dateB = String(b.grade_date || "");
                    return dateA.localeCompare(dateB);
                });
            } else if (type.includes("końc") || type.includes("final")) {
                // Final grade - store separately
                if (!row.finalGrade) row.finalGrade = g;
                else {
                    // Take the most recent final grade
                    const a = String(row.finalGrade.grade_date || "");
                    const b = String(g.grade_date || "");
                    if (b >= a) row.finalGrade = g;
                }
            }
        });

        // z partials robimy 5 slotów
        const result = Array.from(grouped.values()).map((r) => {
            const partialsSorted = [...r.partials].sort(sortByDateAsc);
            const partialSlots = Array(5).fill(null);
            for (let i = 0; i < Math.min(5, partialsSorted.length); i++) {
                partialSlots[i] = partialsSorted[i];
            }

            // Przygotuj strukturę danych dla egzaminów (wszystkie próby dla każdego typu)
            const examGroups = {};
            Object.keys(r.exams || {}).forEach(examType => {
                const examsForType = r.exams[examType];
                if (examsForType && examsForType.length > 0) {
                    examGroups[examType] = examsForType;
                }
            });

            const finalGradeValue = r.finalGrade ? r.finalGrade.grade : null;

            // Calculate average from all grades (excluding final grade)
            // Use final grade if available, otherwise calculate average from all numeric grades
            const avg = finalGradeValue 
                ? null // Don't calculate average if final grade exists
                : computeFinalAverage(r.all);

            return {
                ...r,
                partialSlots,
                examGroups, // wszystkie próby egzaminów pogrupowane według typu
                finalGradeRow: r.finalGrade,
                finalGrade: finalGradeValue ?? null,
                avg,
            };
        });

        // sort: moduł, student
        result.sort((a, b) => {
            const m = a.moduleName.localeCompare(b.moduleName, "pl");
            if (m !== 0) return m;
            return a.studentName.localeCompare(b.studentName, "pl");
        });

        return result;
    }, [grades, selectedGroup, selectedModule, query]);

    const detailsRow = useMemo(() => {
        if (!detailsKey) return null;
        return rows.find((r) => r.key === detailsKey) || null;
    }, [detailsKey, rows]);

    const hasGroupData = groupOptions.length > 0;

    return (
        <div className="lecture-dashboard-panel">
            <LecturerSidebar />
            <ToastNotification
                show={notification.show}
                message={notification.message}
                type={notification.type}
            />

            <div className="main-content">
                <div className="header">
                    <h1 className="header-title">Oceny</h1>
                </div>

                <div className="dashboard">
                    <h2 className="page-title">Oceny</h2>
                    <p className="page-subtitle">Zarządzanie ocenami kursantów</p>

                    <div className="grades-card">
                        <div className="lg-cardTop">
                            <div className="lg-cardTopLeft">
                                <h3 className="lg-h3">Oceny z przedmiotów</h3>
                                <p className="lg-sub">
                                    Wstawianie i przeglądanie ocen z przedmiotów, egzaminów oraz ocen końcowych z przedmiotów.
                                </p>
                            </div>

                            <div className="lg-actions">
                                <button
                                    className="btn button-primary"
                                    onClick={() => setShowCreateGradeModal(true)}
                                    type="button"
                                >
                                    <GraduationCap size={16} style={{ marginRight: "0.5rem" }} />
                                    Dodaj ocenę
                                </button>

                                <button
                                    className="btn button-secondary"
                                    onClick={() => setShowCreateAssignmentModal(true)}
                                    type="button"
                                >
                                    <GraduationCap size={16} style={{ marginRight: "0.5rem" }} />
                                    Dodaj ocenę końcową z przedmiotu
                                </button>

                                <button
                                    className="btn button-secondary lg-toggle"
                                    type="button"
                                    onClick={() => setShowPartials((p) => !p)}
                                    title="Pokaż / ukryj cząstkowe"
                                >
                                    {showPartials ? <EyeOff size={16} /> : <Eye size={16} />}
                                    <span>{showPartials ? "Ukryj cząstkowe" : "Pokaż cząstkowe"}</span>
                                </button>
                            </div>
                        </div>

                        {/* Filtry */}
                        <div className="lg-filters">
                            <div className="lg-filter">
                                <label>Grupa</label>
                                <select
                                    value={selectedGroup}
                                    onChange={(e) => setSelectedGroup(e.target.value)}
                                    disabled={!hasGroupData}
                                >
                                    <option value="">{hasGroupData ? "Wszystkie grupy" : "Brak danych o grupie w API"}</option>
                                    {groupOptions.map((g) => (
                                        <option key={g.value} value={g.value}>
                                            {g.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="lg-filter">
                                <label>Przedmiot / Moduł</label>
                                <select value={selectedModule} onChange={(e) => setSelectedModule(e.target.value)}>
                                    <option value="">Wszystkie</option>
                                    {moduleOptions.map((m) => (
                                        <option key={m} value={m}>
                                            {m}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="lg-filter lg-filterGrow">
                                <label>Szukaj</label>
                                <input
                                    value={query}
                                    onChange={(e) => setQuery(e.target.value)}
                                    placeholder="Imię, nazwisko, indeks, moduł..."
                                />
                            </div>
                        </div>

                        {showCreateGradeModal && (
                            <GradeModal 
                                onClose={() => {
                                    setShowCreateGradeModal(false);
                                    setRefreshTrigger(prev => prev + 1);
                                }} 
                            />
                        )}
                        {showCreateAssignmentModal && (
                            <AssignmentModal 
                                onClose={() => {
                                    setShowCreateAssignmentModal(false);
                                    setRefreshTrigger(prev => prev + 1);
                                }} 
                            />
                        )}

                        {/* Tabela */}
                        <div className={`lg-tableWrap ${showPartials ? "lg-showPartials" : "lg-hidePartials"}`}>
                            <div className="lg-tableHeader">
                                <span>Kursant</span>
                                <span>Numer indeksu</span>
                                <span>Przedmiot</span>

                                <span className="lg-col-partial">Cząstkowa 1</span>
                                <span className="lg-col-partial">Cząstkowa 2</span>
                                <span className="lg-col-partial">Cząstkowa 3</span>
                                <span className="lg-col-partial">Cząstkowa 4</span>
                                <span className="lg-col-partial">Cząstkowa 5</span>

                                <span>Egzamin końcowy</span>
                                <span>Ocena końcowa</span>
                                <span>Data</span>
                            </div>

                            {loadingGrades ? (
                                <p className="loading-text">Ładowanie ocen...</p>
                            ) : rows.length === 0 ? (
                                <p className="no-grades-text">Brak danych do wyświetlenia</p>
                            ) : (
                                <div>
                                    {rows.map((r) => (
                                        <div 
                                            key={r.key} 
                                            className="lg-row lg-row-clickable"
                                            onClick={() => setDetailsKey(r.key)}
                                            title="Kliknij, aby zobaczyć szczegóły i edytować oceny"
                                        >
                                            <span className="lg-name">{safeText(r.studentName)}</span>
                                            <span>{safeText(r.studentNumber)}</span>
                                            <span className="lg-module">{safeText(r.moduleName)}</span>

                                            {r.partialSlots.map((slot, idx) => {
                                                // Determine chip class based on activity type
                                                let chipClass = "";
                                                if (slot && slot.activity_type) {
                                                    const actType = slot.activity_type.toUpperCase();
                                                    if (actType === 'PRESENTATION' || actType === 'ORAL') {
                                                        chipClass = "lg-chip-presentation";
                                                    } else if (actType === 'QUIZ') {
                                                        chipClass = "lg-chip-quiz";
                                                    } else if (actType === 'TEST') {
                                                        chipClass = "lg-chip-test";
                                                    } else if (actType === 'ACTIVITY') {
                                                        chipClass = "lg-chip-activity";
                                                    } else if (actType === 'OTHER') {
                                                        chipClass = "lg-chip-other";
                                                    }
                                                } else if (slot && normalizeType(slot.grade_type).includes("zad")) {
                                                    chipClass = "lg-chip-assignment";
                                                }
                                                
                                                return (
                                                    <span 
                                                        key={idx} 
                                                        className={`lg-col-partial lg-gradeCell ${chipClass ? 'lg-grade-chip' : ''} ${chipClass}`}
                                                        title={slot && slot.activity_type ? getActivityTypeLabel(slot.activity_type) : (slot && slot.assignment_title ? slot.assignment_title : "")}
                                                    >
                                                        {slot ? showGrade(slot) : "—"}
                                                    </span>
                                                );
                                            })}

                                            <span className="lg-gradeCell lg-exam-grade">
                                                {Object.keys(r.examGroups || {}).length > 0 ? (
                                                    Object.keys(r.examGroups).map((examType, typeIdx, typeArray) => {
                                                        const exams = r.examGroups[examType];
                                                        if (!exams || exams.length === 0) return null;
                                                        
                                                        // Sortuj według attempt (rosnąco)
                                                        const sortedExams = [...exams].sort((a, b) => {
                                                            const attemptA = a.attempt || 1;
                                                            const attemptB = b.attempt || 1;
                                                            return attemptA - attemptB;
                                                        });
                                                        
                                                        // Znajdź najwyższą próbę (ostatnią)
                                                        const latestAttempt = Math.max(...sortedExams.map(e => e.attempt || 1));
                                                        
                                                        return (
                                                            <span key={examType} className="lg-exam-type-group">
                                                                <span className="lg-exam-attempts">
                                                                    {sortedExams.map((exam, idx) => {
                                                                        const isLatest = (exam.attempt || 1) === latestAttempt;
                                                                        return (
                                                                            <span 
                                                                                key={exam.id || idx}
                                                                                className={`lg-exam-attempt ${isLatest ? '' : 'lg-exam-strikethrough'}`}
                                                                            >
                                                                                {showGrade(exam)}
                                                                            </span>
                                                                        );
                                                                    })}
                                                                </span>
                                                                {typeIdx < typeArray.length - 1 && (
                                                                    <span className="lg-exam-separator">|</span>
                                                                )}
                                                            </span>
                                                        );
                                                    })
                                                ) : (
                                                    "—"
                                                )}
                                            </span>

                                            <span className={r.finalGradeRow ? "lg-final-grade" : "lg-final-average"}>
                                                {r.finalGradeRow
                                                    ? showGrade(r.finalGradeRow)
                                                    : (r.avg === null ? "—" : `(${r.avg.toFixed(2)})`)}
                                            </span>

                                            <span className="lg-date">{safeText(r.lastDate)}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Modal szczegółów (lista wpisów ocen dla wiersza) */}
                        {detailsRow && (
                            <div className="lg-modalOverlay" onClick={() => setDetailsKey(null)}>
                                <div className="lg-modal" onClick={(e) => e.stopPropagation()}>
                                    <div className="lg-modalHeader">
                                        <div>
                                            <div className="lg-modalTitle">Szczegóły ocen</div>
                                            <div className="lg-modalSub">
                                                <b>{detailsRow.studentName}</b> · {detailsRow.studentNumber} · {detailsRow.moduleName}
                                            </div>
                                        </div>
                                        <button className="lg-close" onClick={() => setDetailsKey(null)} type="button">
                                            <X size={18} />
                                        </button>
                                    </div>

                                    <div className="lg-modalBody">
                                        {(() => {
                                            // Group grades by type
                                            const exams = detailsRow.all.filter(g => normalizeType(g.grade_type).includes('egz'));
                                            const assignments = detailsRow.all.filter(g => normalizeType(g.grade_type).includes('zad') && !g.activity_type);
                                            const activities = detailsRow.all.filter(g => g.activity_type); // Activities have activity_type field
                                            const finalGrades = detailsRow.all.filter(g => 
                                                normalizeType(g.grade_type).includes('końc') || 
                                                normalizeType(g.grade_type).includes('final')
                                            );

                                            return (
                                                <>
                                                    {/* Final Grades Section */}
                                                    {finalGrades.length > 0 && (
                                                        <div className="lg-gradeSection">
                                                            <h4 className="lg-sectionTitle">Ocena końcowa</h4>
                                                            {finalGrades.map((g) => (
                                                                <div key={g.id} className="lg-detailRow">
                                                                    <div className="lg-detailMain">
                                                                        <div className="lg-detailGrade">
                                                                            <span className="lg-gradeVal lg-final-grade lg-final-grade-modal">{showGrade(g)}</span>
                                                                            {g.is_passed !== null && (
                                                                                <span className="lg-statusBadge">
                                                                                    {g.is_passed ? 'Zaliczony' : 'Niezaliczony'}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div className="lg-detailMeta">
                                                                            <span>Data: {safeText(g.grade_date)}</span>
                                                                        </div>
                                                                    </div>
                                                                    <div className="lg-detailActions">
                                                                        <button
                                                                            className="icon-button edit"
                                                                            onClick={() => setEditingGrade(g)}
                                                                            type="button"
                                                                            title="Edytuj"
                                                                        >
                                                                            <Pencil size={18} />
                                                                        </button>
                                                                        <button
                                                                            className="icon-button delete"
                                                                            onClick={() => handleDeleteGrade(g)}
                                                                            type="button"
                                                                            title="Usuń"
                                                                        >
                                                                            <Trash size={18} />
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    )}

                                                    {/* Exams Section */}
                                                    {exams.length > 0 && (() => {
                                                        // Grupuj egzaminy według exam_type
                                                        const examsByType = {};
                                                        exams.forEach(g => {
                                                            const examType = g.exam_type || 'FINAL';
                                                            if (!examsByType[examType]) {
                                                                examsByType[examType] = [];
                                                            }
                                                            examsByType[examType].push(g);
                                                        });

                                                        return (
                                                            <div className="lg-gradeSection">
                                                                <h4 className="lg-sectionTitle">Egzaminy</h4>
                                                                {Object.keys(examsByType).map(examType => {
                                                                    const examsForType = examsByType[examType];
                                                                    // Sortuj według attempt (rosnąco)
                                                                    const sortedExams = [...examsForType].sort((a, b) => {
                                                                        const attemptA = a.attempt || 1;
                                                                        const attemptB = b.attempt || 1;
                                                                        if (attemptA !== attemptB) return attemptA - attemptB;
                                                                        const dateA = String(a.grade_date || "");
                                                                        const dateB = String(b.grade_date || "");
                                                                        return dateA.localeCompare(dateB);
                                                                    });

                                                                    // Build exam chip label
                                                                    let examLabel = 'Egzamin';
                                                                    if (examType === 'FINAL') {
                                                                        examLabel = 'Egzamin końcowy';
                                                                    } else if (examType === 'MIDTERM') {
                                                                        examLabel = 'Egzamin śródsemestralny';
                                                                    } else if (examType === 'RESIT') {
                                                                        examLabel = 'Egzamin poprawkowy';
                                                                    }

                                                                    // Znajdź najwyższą próbę (ostatnią)
                                                                    const latestAttempt = Math.max(...sortedExams.map(e => e.attempt || 1));
                                                                    const latestExam = sortedExams.find(e => (e.attempt || 1) === latestAttempt) || sortedExams[sortedExams.length - 1];

                                                                    return (
                                                                        <div key={examType} className="lg-detailRow">
                                                                            <div className="lg-detailMain">
                                                                                <div className="lg-detailGrade">
                                                                                    <span className={`lg-chip ${examType === 'FINAL' ? 'lg-chip-final-exam' : 'lg-chip-exam'}`}>{examLabel}</span>
                                                                                    <span className={`lg-gradeVal ${examType === 'FINAL' ? 'lg-final-exam-grade' : ''} lg-exam-attempts-modal`}>
                                                                                        {sortedExams.map((g, idx) => {
                                                                                            const isLatest = (g.attempt || 1) === latestAttempt;
                                                                                            return (
                                                                                                <span 
                                                                                                    key={g.id || idx}
                                                                                                    className={`lg-exam-attempt-modal ${isLatest ? '' : 'lg-exam-strikethrough'}`}
                                                                                                >
                                                                                                    {showGrade(g)}
                                                                                                </span>
                                                                                            );
                                                                                        })}
                                                                                    </span>
                                                                                    {latestExam.exam_status && (
                                                                                        <span className={`lg-statusBadge ${latestExam.exam_status === 'PASSED' ? 'lg-status-passed' : 
                                                                                            latestExam.exam_status === 'FAILED' ? 'lg-status-failed' :
                                                                                            latestExam.exam_status === 'ABSENT' ? 'lg-status-absent' :
                                                                                            'lg-status-exempt'}`}>
                                                                                            {latestExam.exam_status === 'PASSED' ? 'Zaliczony' :
                                                                                             latestExam.exam_status === 'FAILED' ? 'Niezaliczony' :
                                                                                             latestExam.exam_status === 'ABSENT' ? 'Nieobecny' :
                                                                                             latestExam.exam_status === 'EXEMPT' ? 'Zwolniony' : latestExam.exam_status}
                                                                                        </span>
                                                                                    )}
                                                                                </div>
                                                                                <div className="lg-detailMeta">
                                                                                    <span>Data: {safeText(latestExam.grade_date)}</span>
                                                                                </div>
                                                                            </div>
                                                                            <div className="lg-detailActions">
                                                                                <button
                                                                                    className="icon-button edit"
                                                                                    onClick={() => setEditingGrade(latestExam)}
                                                                                    type="button"
                                                                                    title="Edytuj"
                                                                                >
                                                                                    <Pencil size={18} />
                                                                                </button>
                                                                                <button
                                                                                    className="icon-button delete"
                                                                                    onClick={() => handleDeleteGrade(latestExam)}
                                                                                    type="button"
                                                                                    title="Usuń"
                                                                                >
                                                                                    <Trash size={18} />
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    );
                                                                })}
                                                            </div>
                                                        );
                                                    })()}

                                                    {/* Assignments Section */}
                                                    {assignments.length > 0 && (
                                                        <div className="lg-gradeSection">
                                                            <h4 className="lg-sectionTitle">Zadania</h4>
                                                            {assignments.sort(sortByDateAsc).map((g) => {
                                                                // Build assignment chip label with title if available
                                                                let assignmentLabel = safeText(g.grade_type);
                                                                if (g.assignment_title) {
                                                                    assignmentLabel = safeText(g.assignment_title);
                                                                }
                                                                
                                                                return (
                                                                    <div key={g.id} className="lg-detailRow">
                                                                        <div className="lg-detailMain">
                                                                            <div className="lg-detailGrade">
                                                                                <span className="lg-chip">{assignmentLabel}</span>
                                                                                <span className="lg-gradeVal">{showGrade(g)}</span>
                                                                            </div>
                                                                            <div className="lg-detailMeta">
                                                                                <span>Data: {safeText(g.grade_date)}</span>
                                                                                {g.feedback && (
                                                                                    <div className="lg-feedback">
                                                                                        <strong>Uwagi:</strong> {safeText(g.feedback)}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="lg-detailActions">
                                                                            <button
                                                                                className="icon-button edit"
                                                                                onClick={() => setEditingGrade(g)}
                                                                                type="button"
                                                                                title="Edytuj"
                                                                            >
                                                                                <Pencil size={18} />
                                                                            </button>
                                                                            <button
                                                                                className="icon-button delete"
                                                                                onClick={() => handleDeleteGrade(g)}
                                                                                type="button"
                                                                                title="Usuń"
                                                                            >
                                                                                <Trash size={18} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* Activities Section */}
                                                    {activities.length > 0 && (
                                                        <div className="lg-gradeSection">
                                                            <h4 className="lg-sectionTitle">Inne aktywności</h4>
                                                            {activities.sort(sortByDateAsc).map((g) => {
                                                                // Build activity chip label using activity_type from backend
                                                                let activityLabel = getActivityTypeLabel(g.activity_type);
                                                                if (g.weight && g.weight !== 1.0) {
                                                                    activityLabel += ` (Waga: ${parseFloat(g.weight).toFixed(2)})`;
                                                                }
                                                                
                                                                // Determine chip color class based on activity type
                                                                let chipColorClass = "lg-chip-activity-default";
                                                                if (g.activity_type) {
                                                                    const actType = g.activity_type.toUpperCase();
                                                                    if (actType === 'PRESENTATION' || actType === 'ORAL') {
                                                                        chipColorClass = "lg-chip-presentation";
                                                                    } else if (actType === 'QUIZ') {
                                                                        chipColorClass = "lg-chip-quiz";
                                                                    } else if (actType === 'TEST') {
                                                                        chipColorClass = "lg-chip-test";
                                                                    } else if (actType === 'ACTIVITY') {
                                                                        chipColorClass = "lg-chip-activity";
                                                                    } else if (actType === 'OTHER') {
                                                                        chipColorClass = "lg-chip-other";
                                                                    }
                                                                }
                                                                
                                                                return (
                                                                    <div key={g.id} className="lg-detailRow">
                                                                        <div className="lg-detailMain">
                                                                            <div className="lg-detailGrade">
                                                                                <span className={`lg-chip ${chipColorClass}`}>{activityLabel}</span>
                                                                                <span className="lg-gradeVal">{showGrade(g)}</span>
                                                                            </div>
                                                                            <div className="lg-detailMeta">
                                                                                <span>Data: {safeText(g.grade_date)}</span>
                                                                                {g.description && (
                                                                                    <div className="lg-description">
                                                                                        <strong>Opis:</strong> {safeText(g.description)}
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                        <div className="lg-detailActions">
                                                                            <button
                                                                                className="icon-button edit"
                                                                                onClick={() => setEditingGrade(g)}
                                                                                type="button"
                                                                                title="Edytuj"
                                                                            >
                                                                                <Pencil size={18} />
                                                                            </button>
                                                                            <button
                                                                                className="icon-button delete"
                                                                                onClick={() => handleDeleteGrade(g)}
                                                                                type="button"
                                                                                title="Usuń"
                                                                            >
                                                                                <Trash size={18} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {exams.length === 0 && assignments.length === 0 && activities.length === 0 && finalGrades.length === 0 && (
                                                        <p style={{ textAlign: 'center', color: '#9ca3af', padding: '2rem' }}>
                                                            Brak ocen do wyświetlenia
                                                        </p>
                                                    )}
                                                </>
                                            );
                                        })()}
                                    </div>

                                    <div className="lg-modalFooter">
                                        <span className="lg-note">
                                            * Ocena końcowa jest liczona jako średnia z wszystkich dostępnych ocen liczbowych (egzaminy, zadania, aktywności).
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {editingGrade && (
                            <EditGradeModal
                                grade={editingGrade}
                                onClose={() => setEditingGrade(null)}
                                onSave={handleSaveGrade}
                            />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
